/**
 * Simulation Launch Modal - V5
 * Combines V4's loading progress features with V5's Tailwind design
 * Shows progress bar, fading tips/challenges, and step countdown timers
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, CheckCircle2, Clock, Users, Target, Rocket, AlertCircle,
    Lightbulb, Flag, RefreshCw, Hourglass
} from 'lucide-react';

interface Simulation {
    id: string;
    title: string;
    description: string;
    difficulty: string;
    duration: string;
    role: string;
    skills_evaluated: string[];
    thumbnail_color: string;
    tags?: string[];
}

interface SimulationDetails {
    id: string;
    title: string;
    description: string;
    role: string;
    primary_objective: string;
    primary_conflicts: string[];
    user_goals: string[];
    simulation_type?: string;
    enable_real_world_problems?: boolean;
}

interface SimulationLaunchModalProps {
    simulation: Simulation;
    simulationDetails: SimulationDetails | null;
    isPreparing: boolean;
    isReady: boolean;
    onClose: () => void;
    onStart: () => void;
    onCancel: () => void;
}

type SimulationType = 'conversation' | 'conversation_with_problems' | 'coding' | 'hre_enabled';

interface InitStep {
    id: string;
    label: string;
    maxTimeSeconds: number;
    status: 'pending' | 'active' | 'completed';
}

const PRO_TIPS = [
    "Balance empathy and results — your choices have ripple effects.",
    "Each conversation reveals clues to completing your objectives.",
    "The more natural and thoughtful your responses, the better your outcome.",
    "There's no single \"right\" answer — it's about how you lead under pressure."
];

const CHALLENGES = [
    "Complete all objectives within the given time.",
    "You can alternate between Conversations and some conversations could be interlinked.",
    "Every interaction moves you closer to success OR costs you valuable progress."
];

const TIMING_CONFIGS: Record<SimulationType, { blueprint: number; tasks: number; personas: number; conversations: number; finalize: number }> = {
    conversation: {
        blueprint: 5,
        tasks: 15,
        personas: 50,
        conversations: 15,
        finalize: 5
    },
    conversation_with_problems: {
        blueprint: 5,
        tasks: 15,
        personas: 55,
        conversations: 15,
        finalize: 10
    },
    coding: {
        blueprint: 5,
        tasks: 20,
        personas: 55,
        conversations: 30,
        finalize: 10
    },
    hre_enabled: {
        blueprint: 5,
        tasks: 10,
        personas: 30,
        conversations: 10,
        finalize: 5
    }
};

function determineSimulationType(
    simulationType?: string,
    enableRealWorldProblems?: boolean,
    useHRE?: boolean
): SimulationType {
    if (useHRE) {
        return 'hre_enabled';
    }
    if (simulationType === 'coding' || simulationType === 'hybrid') {
        return 'coding';
    }
    if (enableRealWorldProblems) {
        return 'conversation_with_problems';
    }
    return 'conversation';
}

export function SimulationLaunchModal({
    simulation,
    simulationDetails,
    isPreparing,
    isReady,
    onClose,
    onStart,
    onCancel
}: SimulationLaunchModalProps) {
    const [showReadyState, setShowReadyState] = useState(false);

    const simType = determineSimulationType(
        simulationDetails?.simulation_type,
        simulationDetails?.enable_real_world_problems,
        false
    );
    const timingConfig = TIMING_CONFIGS[simType];

    const createInitSteps = (): InitStep[] => [
        { id: 'blueprint', label: 'Loading simulation structure', maxTimeSeconds: timingConfig.blueprint, status: 'pending' },
        { id: 'tasks', label: 'Setting up objectives and tasks', maxTimeSeconds: timingConfig.tasks, status: 'pending' },
        { id: 'personas', label: 'Gathering characters', maxTimeSeconds: timingConfig.personas, status: 'pending' },
        { id: 'conversations', label: 'Setting up conversations', maxTimeSeconds: timingConfig.conversations, status: 'pending' },
        { id: 'finalize', label: 'Finalizing simulation', maxTimeSeconds: timingConfig.finalize, status: 'pending' }
    ];

    const [steps, setSteps] = useState<InitStep[]>(createInitSteps());
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [showingTips, setShowingTips] = useState(true);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const totalMaxTime = steps.reduce((sum, step) => sum + step.maxTimeSeconds, 0);

    const calculateProgress = () => {
        const completedTime = steps
            .slice(0, currentStepIndex)
            .reduce((sum, step) => sum + step.maxTimeSeconds, 0);

        const currentStepProgress = currentStepIndex < steps.length
            ? Math.min(elapsedSeconds - completedTime, steps[currentStepIndex].maxTimeSeconds)
            : 0;

        return ((completedTime + currentStepProgress) / totalMaxTime) * 100;
    };

    const progress = calculateProgress();

    useEffect(() => {
        if (!isPreparing || isReady || showReadyState) return;

        const interval = setInterval(() => {
            setElapsedSeconds(prev => prev + 0.1);
        }, 100);

        return () => clearInterval(interval);
    }, [isPreparing, isReady, showReadyState]);

    useEffect(() => {
        if (showReadyState) return;

        let cumulativeTime = 0;
        const newSteps = steps.map((step) => {
            const stepEndTime = cumulativeTime + step.maxTimeSeconds;

            let status: 'pending' | 'active' | 'completed';
            if (elapsedSeconds >= stepEndTime) {
                status = 'completed';
            } else if (elapsedSeconds >= cumulativeTime) {
                status = 'active';
            } else {
                status = 'pending';
            }

            cumulativeTime = stepEndTime;
            return { ...step, status };
        });

        setSteps(newSteps);

        const activeIndex = newSteps.findIndex(s => s.status === 'active');
        if (activeIndex !== -1 && activeIndex !== currentStepIndex) {
            setCurrentStepIndex(activeIndex);
        } else if (newSteps.every(s => s.status === 'completed')) {
            setCurrentStepIndex(newSteps.length);
        }
    }, [elapsedSeconds, showReadyState]);

    useEffect(() => {
        if (!isPreparing || isReady || showReadyState) return;

        const tipDuration = 5000;
        const interval = setInterval(() => {
            if (showingTips) {
                if (currentTipIndex < PRO_TIPS.length - 1) {
                    setCurrentTipIndex(prev => prev + 1);
                } else {
                    setShowingTips(false);
                    setCurrentTipIndex(0);
                }
            } else {
                if (currentTipIndex < CHALLENGES.length - 1) {
                    setCurrentTipIndex(prev => prev + 1);
                } else {
                    setShowingTips(true);
                    setCurrentTipIndex(0);
                }
            }
        }, tipDuration);

        return () => clearInterval(interval);
    }, [currentTipIndex, showingTips, isPreparing, isReady, showReadyState]);

    useEffect(() => {
        if (isReady) {
            setTimeout(() => setShowReadyState(true), 500);
        }
    }, [isReady]);

    const objectives = simulationDetails?.user_goals || [];
    const challenges = simulationDetails?.primary_conflicts || [];
    const userGoalsCount = objectives.length || 6;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                >
                    {/* Header with gradient */}
                    <div className="relative h-32 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 overflow-hidden">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20"></div>

                        <button
                            onClick={onCancel}
                            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors duration-200 text-white"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="relative h-full flex items-center justify-center">
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="text-center"
                            >
                                <h2 className="text-3xl font-bold text-white">{simulation.title}</h2>
                                <p className="text-emerald-100 mt-1">
                                    {!showReadyState ? 'Initializing Simulation...' : 'Ready to Begin'}
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-8 overflow-y-auto max-h-[calc(90vh-8rem)]">
                        <AnimatePresence mode="wait">
                            {!showReadyState ? (
                                <motion.div
                                    key="preparing"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="space-y-6"
                                >
                                    {/* Two-column layout */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* LEFT COLUMN - Tips and Challenges */}
                                        <div className="flex flex-col justify-center min-h-[200px] relative">
                                            <AnimatePresence mode="wait">
                                                {showingTips ? (
                                                    <motion.div
                                                        key={`tip-${currentTipIndex}`}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        transition={{ duration: 0.5 }}
                                                        className="space-y-3"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                                                                <Lightbulb className="w-5 h-5 text-yellow-600" />
                                                            </div>
                                                            <h4 className="font-semibold text-slate-900">Pro Tip</h4>
                                                        </div>
                                                        <p className="text-slate-700 leading-relaxed">
                                                            {PRO_TIPS[currentTipIndex]}
                                                        </p>
                                                        <div className="flex gap-1 pt-2">
                                                            {PRO_TIPS.map((_, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                                        idx === currentTipIndex ? 'bg-yellow-500 w-4' : 'bg-slate-300'
                                                                    }`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                ) : (
                                                    <motion.div
                                                        key={`challenge-${currentTipIndex}`}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        transition={{ duration: 0.5 }}
                                                        className="space-y-3"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                                                <Flag className="w-5 h-5 text-orange-600" />
                                                            </div>
                                                            <h4 className="font-semibold text-slate-900">Your Challenge</h4>
                                                        </div>
                                                        <p className="text-slate-700 leading-relaxed">
                                                            {CHALLENGES[currentTipIndex].replace('all objectives', `all ${userGoalsCount} objectives`)}
                                                        </p>
                                                        <div className="flex gap-1 pt-2">
                                                            {CHALLENGES.map((_, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                                        idx === currentTipIndex ? 'bg-orange-500 w-4' : 'bg-slate-300'
                                                                    }`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        {/* RIGHT COLUMN - Progress Steps */}
                                        <div className="space-y-4">
                                            {/* Overall Progress */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-semibold text-slate-900">Progress</span>
                                                    <span className="text-sm font-bold text-emerald-600">{Math.round(progress)}%</span>
                                                </div>

                                                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                                    <motion.div
                                                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${progress}%` }}
                                                        transition={{ duration: 0.3 }}
                                                    />
                                                </div>

                                                <div className="flex justify-between text-xs text-slate-500">
                                                    <span>Estimated: ~{totalMaxTime}s</span>
                                                    <span>{Math.round(elapsedSeconds)}s elapsed</span>
                                                </div>
                                            </div>

                                            {/* Step List */}
                                            <div className="space-y-3 pt-2">
                                                {steps.map((step, index) => (
                                                    <div
                                                        key={step.id}
                                                        className={`flex items-center gap-3 transition-opacity duration-300 ${
                                                            step.status === 'pending' ? 'opacity-50' : 'opacity-100'
                                                        }`}
                                                    >
                                                        {/* Status Icon */}
                                                        {step.status === 'completed' ? (
                                                            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                                        ) : step.status === 'active' ? (
                                                            <motion.div
                                                                animate={{ rotate: 360 }}
                                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                            >
                                                                <RefreshCw className="w-5 h-5 text-emerald-500" />
                                                            </motion.div>
                                                        ) : (
                                                            <Hourglass className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                                        )}

                                                        {/* Step Label */}
                                                        <span
                                                            className={`text-sm flex-1 ${
                                                                step.status === 'completed'
                                                                    ? 'text-emerald-600 font-medium'
                                                                    : step.status === 'active'
                                                                    ? 'text-emerald-700 font-semibold'
                                                                    : 'text-slate-500'
                                                            }`}
                                                        >
                                                            {step.label}
                                                        </span>

                                                        {/* Timer for active step */}
                                                        {step.status === 'active' && (
                                                            <motion.span
                                                                animate={{ opacity: [0.6, 1, 0.6] }}
                                                                transition={{ duration: 1.5, repeat: Infinity }}
                                                                className="text-xs text-emerald-600 font-semibold"
                                                            >
                                                                {Math.max(0, Math.round(
                                                                    step.maxTimeSeconds -
                                                                    (elapsedSeconds - steps.slice(0, index).reduce((sum, s) => sum + s.maxTimeSeconds, 0))
                                                                ))}s
                                                            </motion.span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Cancel Button */}
                                    <div className="pt-4 border-t border-slate-200">
                                        <button
                                            onClick={onCancel}
                                            className="w-full px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors duration-200"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="ready"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="space-y-6"
                                >
                                    {/* Ready Banner */}
                                    <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                        <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                                            <CheckCircle2 className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-emerald-900">Ready to Begin!</div>
                                            <div className="text-sm text-emerald-700">Your simulation is prepared and waiting</div>
                                        </div>
                                    </div>

                                    {/* Role */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <Users className="w-4 h-4 text-slate-600" />
                                            </div>
                                            <h4 className="font-semibold text-slate-900">Your Role</h4>
                                        </div>
                                        <p className="text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">
                                            {simulation.role}
                                        </p>
                                    </div>

                                    {/* Objectives */}
                                    {objectives.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <Target className="w-4 h-4 text-blue-600" />
                                                </div>
                                                <h4 className="font-semibold text-slate-900">Objectives</h4>
                                            </div>
                                            <div className="space-y-2">
                                                {objectives.map((objective, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.1 }}
                                                        className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                                                    >
                                                        <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                        <span className="text-sm text-slate-700">{objective}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Challenges */}
                                    {challenges.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                                    <AlertCircle className="w-4 h-4 text-orange-600" />
                                                </div>
                                                <h4 className="font-semibold text-slate-900">Key Challenges</h4>
                                            </div>
                                            <div className="space-y-2">
                                                {challenges.map((challenge, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.2 + index * 0.1 }}
                                                        className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg"
                                                    >
                                                        <div className="w-5 h-5 bg-orange-200 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                                                            <span className="text-xs font-bold text-orange-700">{index + 1}</span>
                                                        </div>
                                                        <span className="text-sm text-slate-700">{challenge}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Duration */}
                                    <div className="flex items-center gap-2 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                        <Clock className="w-5 h-5 text-purple-600" />
                                        <div>
                                            <span className="text-sm font-medium text-purple-900">Expected Duration: </span>
                                            <span className="text-sm text-purple-700">{simulation.duration}</span>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 pt-4">
                                        <button
                                            onClick={onClose}
                                            className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors duration-200"
                                        >
                                            Not Yet
                                        </button>
                                        <motion.button
                                            onClick={onStart}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
                                        >
                                            <Rocket className="w-5 h-5" />
                                            Start Simulation
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
